/**
 * Typed wrappers around the /api/v1/auth/* + /api/v1/me endpoints.
 * Paths and shapes are taken verbatim from backend/internal/router and
 * backend/internal/handlers/auth.go.
 */

import { api, clearCSRFToken, setCSRFToken, type RetriableConfig } from "./client";
import {
  isSelectWorkspace,
  type ActivateRequest,
  type ForgotPasswordRequest,
  type ForgotPasswordResponse,
  type RecoveryOptions,
  type LoginRequest,
  type LoginResponse,
  type LoginResult,
  type MeResponse,
  type MyWorkspacesResponse,
  type RegisterRequest,
  type RegisterResponse,
  type ResetPasswordRequest,
  type ResetPasswordResponse,
  type DeclineSigninResponse,
  type TOTPSetupResponse,
  type TOTPVerifyRequest,
  type TwoFactorMethodsResponse,
} from "@/types/auth";

// POST /api/v1/auth/register — 201 (OptionalAuth).
//
// ID.2: register auto-activates the just-created company and returns a
// FULL tenant-scoped loginResponse + session cookies (may be
// totp_required). With an identity session cookie present it ATTACHES
// a new company to that identity ("add another company"); a new email
// creates a fresh identity. An existing email with no/foreign session
// → 409 identity_exists (handled by the caller).
export async function register(
  body: RegisterRequest,
): Promise<RegisterResponse> {
  const { data } = await api.post<RegisterResponse>(
    "/api/v1/auth/register",
    body,
  );
  // Cross-origin staging fallback: backend's wr_csrf cookie is host-only
  // so document.cookie can't see it; stash the token from the body
  // (data.csrf_token) into the in-memory store so subsequent writes
  // include the X-CSRF-Token header. No-op on a same-origin deploy
  // where the cookie itself is readable.
  setCSRFToken(data.csrf_token);
  return data;
}

// POST /api/v1/auth/login — 200. Identity auth: email + password only
// (NO company_slug).
//
// Two 200 shapes (see backend auth.go):
//  - normal: loginResponse + Set-Cookie (wr_access/wr_refresh/wr_csrf),
//    possibly totp_required. Single membership OR platform identity.
//  - { select_workspace:true, workspaces, totp_required } + an
//    IDENTITY-ONLY session cookie (no active tenant), when the identity
//    has >=2 active memberships. The caller opens the picker and POSTs
//    the chosen slug to /auth/activate — no selection token.
// `isSelectWorkspace()` (types/auth) is the discriminant.
export async function login(body: LoginRequest): Promise<LoginResult> {
  const { data } = await api.post<LoginResult>("/api/v1/auth/login", body);
  // Stash the CSRF token from the body on the LoginResponse branch
  // (the picker / SelectWorkspaceResponse branch has no body CSRF
  // because no tenant is active yet — /activate writes it). See
  // setCSRFToken docstring for the cross-origin rationale.
  if (!isSelectWorkspace(data)) {
    setCSRFToken(data.csrf_token);
  }
  return data;
}

// POST /api/v1/auth/activate — 200 (authed: identity-only OR active
// session; CSRF-exempt). THE single endpoint that establishes/switches
// the active tenant: the post-login picker target AND the in-app
// instant switch (no password, no logout). On success returns the
// IDENTICAL loginResponse + full (rotated) tenant cookies as a normal
// login. Errors: 403 not_a_member | platform_identity |
// account_inactive (all generic, no enumeration).
export async function activate(
  body: ActivateRequest,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>(
    "/api/v1/auth/activate",
    body,
  );
  // /activate rotates cookies + returns a fresh csrf_token in the body —
  // re-stash for the cross-origin staging fallback (same logic as login
  // + register; see setCSRFToken docstring).
  setCSRFToken(data.csrf_token);
  return data;
}

// GET /api/v1/auth/my-workspaces — 200 (auth required, GET → no CSRF).
// Identity-scoped: the caller's current workspace (if an active tenant)
// plus their OTHER memberships. Drives both the in-app switcher and the
// identity-only "pick a workspace" state.
export async function getMyWorkspaces(): Promise<MyWorkspacesResponse> {
  const { data } = await api.get<MyWorkspacesResponse>(
    "/api/v1/auth/my-workspaces",
  );
  return data;
}

// POST /api/v1/auth/logout — 200 (auth + CSRF required)
export async function logout(): Promise<void> {
  await api.post("/api/v1/auth/logout");
  // Clear the in-memory CSRF too so the next anonymous request doesn't
  // attempt to use a now-revoked token. The cookie is cleared by the
  // backend's Set-Cookie; this is the cross-origin counterpart.
  clearCSRFToken();
}

// GET /api/v1/me — 200 (auth required, no CSRF — safe method)
//
// `probe: true` is for the mount-time identity check: it sets
// _suppressAuthRedirect, so a 401 tries one shared refresh and replays
// (an expired access cookie with a good refresh cookie is still a
// session), and otherwise rejects quietly with no /login bounce —
// letting AuthProvider set user=null for visitors on public pages like
// /register or /forgot.
export async function getMe(opts?: { probe?: boolean }): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>("/api/v1/me", {
    _suppressAuthRedirect: opts?.probe === true,
  } as RetriableConfig);
  return data;
}

// PUT /api/v1/me/preferred-language — 200 (auth + CSRF). Q4 H1: the
// person's POSH training language, remembered on their own membership.
// Anything but "en" / "hi" is 400 invalid_language.
export async function setPreferredLanguage(
  language: "en" | "hi",
): Promise<{ preferred_language: "en" | "hi" }> {
  const { data } = await api.put<{ preferred_language: "en" | "hi" }>(
    "/api/v1/me/preferred-language",
    { language },
  );
  return data;
}

// POST /api/v1/auth/forgot-password — 200 (always success-shaped)
// GET /api/v1/auth/recovery-options — public; no account data.
export async function getRecoveryOptions(signal?: AbortSignal): Promise<RecoveryOptions> {
  const { data } = await api.get<RecoveryOptions>("/api/v1/auth/recovery-options", { signal });
  return data;
}

export async function forgotPassword(
  body: ForgotPasswordRequest,
): Promise<ForgotPasswordResponse> {
  const { data } = await api.post<ForgotPasswordResponse>(
    "/api/v1/auth/forgot-password",
    body,
  );
  return data;
}

// POST /api/v1/auth/reset-password — 200
export async function resetPassword(
  body: ResetPasswordRequest,
): Promise<ResetPasswordResponse> {
  const { data } = await api.post<ResetPasswordResponse>("/api/v1/auth/reset-password", body);
  return data;
}

// POST /api/v1/auth/decline-signin — PUBLIC, token-gated like reset: the
// person doesn't know the company that added them (confirm link).
export async function declineSignin(body: {
  email: string;
  token: string;
}): Promise<DeclineSigninResponse> {
  const { data } = await api.post<DeclineSigninResponse>("/api/v1/auth/decline-signin", body);
  return data;
}

// POST /api/v1/auth/resend-invite — PUBLIC, always 200 with the SAME neutral
// message (no account oracle). Re-sends a fresh set-password link when the
// email has a pending (never-used) invitation; otherwise does nothing.
export async function resendInvite(
  email: string,
): Promise<{ message: string; email_delivery?: "sent" | "unavailable" }> {
  const { data } = await api.post<{ message: string; email_delivery?: "sent" | "unavailable" }>(
    "/api/v1/auth/resend-invite",
    { email },
  );
  return data;
}

// POST /api/v1/auth/change-password — 200 (auth required). Requires the
// CURRENT password as proof of ownership; on success the backend revokes
// every other session and re-issues cookies for THIS client, and clears
// must_change_password (099).
export async function changePassword(body: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  const { data } = await api.post<{ csrf_token?: string }>(
    "/api/v1/auth/change-password",
    body,
  );
  // The backend revoked every session (incl. the CSRF row) and minted a
  // fresh token for this client — stash it like login/activate do.
  if (data.csrf_token) setCSRFToken(data.csrf_token);
}

// POST /api/v1/auth/totp/setup — 200 (auth + CSRF required)
export async function totpSetup(): Promise<TOTPSetupResponse> {
  const { data } = await api.post<TOTPSetupResponse>(
    "/api/v1/auth/totp/setup",
  );
  return data;
}

// POST /api/v1/auth/totp/verify — 200 (auth + CSRF required)
// On success the backend re-issues wr_access with the totp=true claim.
// Completing a pending sign-in rotates the session, so the body may carry
// a fresh csrf_token — stash it like login/activate do.
export async function totpVerify(body: TOTPVerifyRequest): Promise<SecondFactorResponse | undefined> {
  const { data } = await api.post<SecondFactorResponse | undefined>("/api/v1/auth/totp/verify", body);
  if (data?.csrf_token) setCSRFToken(data.csrf_token);
  return data;
}

/** Body of a completed second factor (code or passkey): status "ok", plus
 *  the new session's csrf_token when the session was rotated. */
export interface SecondFactorResponse {
  status: "ok";
  csrf_token?: string;
  /** Companies this sign-in just finished joining (the email confirm link
   *  was used, then the person's own two-step method). */
  joined_companies?: string[];
}

/** A WebAuthn ceremony start: the single-use challenge id to echo back and
 *  go-webauthn's CredentialCreation / CredentialAssertion JSON (the
 *  browser options sit under `publicKey`). */
export interface PasskeyChallenge<T = Record<string, unknown>> {
  challenge_id: string;
  options: { publicKey: T; mediation?: string };
}

// GET /api/v1/auth/2fa/methods — the caller's own second factors (full
// session only; a pending-2FA session reads `methods` from /me instead).
// `skipStepUp` keeps a 401 totp_required from opening the step-up prompt
// (the prompt itself asks for this list).
export async function getTwoFactorMethods(opts?: { skipStepUp?: boolean }): Promise<TwoFactorMethodsResponse> {
  const { data } = await api.get<TwoFactorMethodsResponse>("/api/v1/auth/2fa/methods", {
    _stepUpReplayed: opts?.skipStepUp === true,
  } as RetriableConfig);
  return data;
}

// POST /api/v1/auth/passkey/login/begin — public passwordless sign-in
// challenge (discoverable credentials: no email first).
export async function passkeyLoginBegin(): Promise<PasskeyChallenge> {
  const { data } = await api.post<PasskeyChallenge>("/api/v1/auth/passkey/login/begin", {});
  return data;
}

// POST /api/v1/auth/passkey/login/finish — the same response union as
// /auth/login (session / picker / 403 totp_enrollment_required).
// Errors: 401 passkey_invalid, 429 locked, 403 account_inactive.
export async function passkeyLoginFinish(body: { challenge_id: string; credential: unknown }): Promise<LoginResult> {
  const { data } = await api.post<LoginResult>("/api/v1/auth/passkey/login/finish", body);
  if (!isSelectWorkspace(data)) setCSRFToken(data.csrf_token);
  return data;
}

// POST /api/v1/auth/passkey/verify/begin|finish — the session's own
// passkey as the second factor after a password, or as a step-up.
export async function passkeyVerifyBegin(): Promise<PasskeyChallenge> {
  const { data } = await api.post<PasskeyChallenge>("/api/v1/auth/passkey/verify/begin", {});
  return data;
}

export async function passkeyVerifyFinish(body: { challenge_id: string; credential: unknown }): Promise<SecondFactorResponse> {
  const { data } = await api.post<SecondFactorResponse>("/api/v1/auth/passkey/verify/finish", body);
  if (data?.csrf_token) setCSRFToken(data.csrf_token);
  return data;
}

// POST /api/v1/auth/passkey/register/begin|finish — add a passkey. 403
// step_up_required when the person already has a method and the session
// has no fresh step-up. A FIRST method also completes the session's 2FA
// (status "ok" + maybe csrf_token), like confirming an authenticator.
export async function passkeyRegisterBegin(): Promise<PasskeyChallenge> {
  const { data } = await api.post<PasskeyChallenge>("/api/v1/auth/passkey/register/begin", {});
  return data;
}

export interface PasskeyRegistered {
  id: number;
  name: string;
  status?: "ok";
  csrf_token?: string;
}

export async function passkeyRegisterFinish(body: {
  challenge_id: string;
  name: string;
  credential: unknown;
}): Promise<PasskeyRegistered> {
  const { data } = await api.post<PasskeyRegistered>("/api/v1/auth/passkey/register/finish", body);
  if (data.csrf_token) setCSRFToken(data.csrf_token);
  return data;
}

// POST /api/v1/auth/totp/disable — remove my authenticator app. JSON-only
// identity route; a full membership session must also pass the CSRF check
// (in the handler — the request interceptor sends X-CSRF-Token on every
// unsafe method). The proof is the session: 2FA completed and
// a fresh step-up (403 step_up_required otherwise). 409 last_2fa_method when
// it is the last method of someone who must have 2FA. Every other device is
// signed out and THIS session is re-issued — adopt the new csrf_token.
export async function totpDisable(): Promise<void> {
  const { data } = await api.post<{ status: string; csrf_token?: string }>(
    "/api/v1/auth/totp/disable",
    {},
  );
  if (data?.csrf_token) setCSRFToken(data.csrf_token);
}

// POST /api/v1/auth/dpdp-consent — 200 (auth + CSRF required; no body).
// Records the per-membership DPDP 2023 employee consent. The backend
// stores `EMPLOYEE_DPDP_CONSENT_PURPOSE` verbatim into
// `users.consent_purpose`; the response echoes that same string so the
// caller can sanity-check the lockstep. Fails closed for identity-only
// / platform sessions (no active membership ⇒ 403 tenant_required).
export async function dpdpConsent(): Promise<{ status: string; purpose: string }> {
  const { data } = await api.post<{ status: string; purpose: string }>(
    "/api/v1/auth/dpdp-consent",
  );
  return data;
}
